import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import { adminLogin, adminListEnquiries, adminUpdateStatus, adminDeleteEnquiry } from "../lib/api";

const TOKEN_KEY = "fg_admin_token";
const HTTP_UNAUTHORIZED = 401;

export default function Admin() {
    const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [enquiries, setEnquiries] = useState([]);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            const data = await adminListEnquiries(token);
            setEnquiries(data);
        } catch (err) {
            if (err?.response?.status === HTTP_UNAUTHORIZED) {
                sessionStorage.removeItem(TOKEN_KEY);
                setToken("");
                toast.error("Session expired. Please log in again.");
            } else {
                toast.error("Could not load enquiries.");
            }
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (token) load();
    }, [token, load]);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!password) return;
        setLoading(true);
        try {
            const res = await adminLogin(password);
            sessionStorage.setItem(TOKEN_KEY, res.token);
            setToken(res.token);
            toast.success("Welcome back.");
        } catch (err) {
            toast.error("Invalid password.");
        } finally {
            setLoading(false);
        }
    };

    const onStatus = async (id, status) => {
        try {
            await adminUpdateStatus(token, id, status);
            setEnquiries((arr) => arr.map((e) => (e.id === id ? { ...e, status } : e)));
            toast.success(`Marked ${status}.`);
        } catch {
            toast.error("Update failed.");
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this enquiry permanently?")) return;
        try {
            await adminDeleteEnquiry(token, id);
            setEnquiries((arr) => arr.filter((e) => e.id !== id));
            setSelected(null);
            toast.success("Deleted.");
        } catch {
            toast.error("Delete failed.");
        }
    };

    const logout = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setEnquiries([]);
    };

    const filtered = enquiries.filter((e) => (filter === "all" ? true : e.status === filter));

    if (!token) {
        return (
            <main className="min-h-screen pt-32 md:pt-40 pb-24 px-6 md:px-10" data-testid="admin-login-page">
                <div className="max-w-md mx-auto bg-[#FAF9F6] border border-black/10 p-10 md:p-12">
                    <div className="flex items-center gap-3 text-[#1B4332]">
                        <Lock size={20} />
                        <span className="text-[11px] uppercase tracking-[0.32em]">Admin Area</span>
                    </div>
                    <h1 className="font-display text-4xl mt-5 leading-tight">Sign in to review enquiries.</h1>
                    <form onSubmit={handleLogin} className="mt-8" data-testid="admin-login-form">
                        <label className="text-[10px] uppercase tracking-[0.3em] text-[#1A1A1A]/55">Password</label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-login-password"
                            className="w-full bg-transparent border-b border-[#1A1A1A]/25 py-3 mt-2 text-[15px] focus:outline-none focus:border-[#1B4332]"
                            placeholder="••••••••"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="admin-login-submit"
                            className="mt-8 w-full bg-[#1B4332] text-[#FAF9F6] px-6 py-3 text-[12px] uppercase tracking-[0.26em] hover:bg-[#112d21] transition-all disabled:opacity-60"
                        >
                            {loading ? "Signing in…" : "Sign In"}
                        </button>
                    </form>
                </div>
            </main>
        );
    }

    return (
        <main className="pt-28 md:pt-32 pb-24" data-testid="admin-dashboard">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                {/* HEADER */}
                <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-3 text-[#1B4332]">
                            <ShieldCheck size={16} />
                            <span className="text-[11px] uppercase tracking-[0.32em]">Enquiry Console</span>
                        </div>
                        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                            {enquiries.length} enquiries received
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={load}
                            data-testid="admin-refresh"
                            className="inline-flex items-center gap-2 border border-[#1B4332]/30 px-4 py-2 text-[11px] uppercase tracking-[0.26em] text-[#1B4332] hover:bg-[#1B4332] hover:text-[#FAF9F6] transition-colors"
                        >
                            <RefreshCw size={14} /> Refresh
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="border border-black/15 px-4 py-2 text-[11px] uppercase tracking-[0.26em] text-[#1A1A1A]/75 hover:bg-[#1A1A1A] hover:text-[#FAF9F6] transition-colors"
                        >
                            Sign Out
                        </button>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-filters">
                    {[
                        { k: "all", label: `All (${enquiries.length})` },
                        { k: "new", label: `New (${enquiries.filter((e) => e.status === "new").length})` },
                        { k: "reviewed", label: `Reviewed (${enquiries.filter((e) => e.status === "reviewed").length})` },
                        { k: "archived", label: `Archived (${enquiries.filter((e) => e.status === "archived").length})` },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilter(f.k)}
                            data-testid={`admin-filter-${f.k}`}
                            className={`px-4 py-2 text-[11px] uppercase tracking-[0.24em] border transition-colors ${
                                filter === f.k
                                    ? "bg-[#1B4332] text-[#FAF9F6] border-[#1B4332]"
                                    : "border-black/15 text-[#1A1A1A]/70 hover:border-[#1B4332]"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>

                {/* TABLE */}
                <div className="border border-black/10 bg-[#FAF9F6] overflow-x-auto" data-testid="admin-enquiries-table">
                    <table className="w-full text-[14px]">
                        <thead className="bg-[#F3F1EC] text-[10px] uppercase tracking-[0.24em] text-[#1A1A1A]/65">
                            <tr>
                                <th className="text-left py-4 px-5">Received</th>
                                <th className="text-left py-4 px-5">Name</th>
                                <th className="text-left py-4 px-5">Service</th>
                                <th className="text-left py-4 px-5">Contact</th>
                                <th className="text-left py-4 px-5">Guests</th>
                                <th className="text-left py-4 px-5">Status</th>
                                <th className="text-right py-4 px-5">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-16 text-[#1A1A1A]/55">
                                        No enquiries to show.
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((e) => (
                                    <tr
                                        key={e.id}
                                        data-testid={`admin-row-${e.id}`}
                                        className="border-t border-black/5 hover:bg-[#F3F1EC]/50 cursor-pointer"
                                        onClick={() => setSelected(e)}
                                    >
                                        <td className="py-4 px-5 text-[#1A1A1A]/65">
                                            {new Date(e.created_at).toLocaleString()}
                                        </td>
                                        <td className="py-4 px-5">
                                            <div className="font-medium">{e.name}</div>
                                            {e.company && <div className="text-[12px] text-[#1A1A1A]/55">{e.company}</div>}
                                        </td>
                                        <td className="py-4 px-5">{e.service_type}</td>
                                        <td className="py-4 px-5">
                                            <div>{e.email}</div>
                                            <div className="text-[12px] text-[#1A1A1A]/55">{e.phone}</div>
                                        </td>
                                        <td className="py-4 px-5">{e.guest_count ?? "—"}</td>
                                        <td className="py-4 px-5">
                                            <StatusPill status={e.status} />
                                        </td>
                                        <td className="py-4 px-5 text-right" onClick={(ev) => ev.stopPropagation()}>
                                            <div className="inline-flex gap-2">
                                                {e.status !== "reviewed" && (
                                                    <button
                                                        onClick={() => onStatus(e.id, "reviewed")}
                                                        data-testid={`admin-mark-reviewed-${e.id}`}
                                                        className="text-[10px] uppercase tracking-[0.24em] border border-[#1B4332]/40 px-3 py-1.5 hover:bg-[#1B4332] hover:text-[#FAF9F6]"
                                                    >
                                                        Mark Reviewed
                                                    </button>
                                                )}
                                                {e.status !== "archived" && (
                                                    <button
                                                        onClick={() => onStatus(e.id, "archived")}
                                                        data-testid={`admin-archive-${e.id}`}
                                                        className="text-[10px] uppercase tracking-[0.24em] border border-black/15 px-3 py-1.5 hover:bg-[#1A1A1A] hover:text-[#FAF9F6]"
                                                    >
                                                        Archive
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => onDelete(e.id)}
                                                    data-testid={`admin-delete-${e.id}`}
                                                    className="text-[#C84B31] border border-[#C84B31]/40 p-1.5 hover:bg-[#C84B31] hover:text-[#FAF9F6]"
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* DETAIL DRAWER */}
            {selected && (
                <div
                    className="fixed inset-0 z-50 flex justify-end"
                    data-testid="admin-detail-drawer"
                    onClick={() => setSelected(null)}
                >
                    <div className="absolute inset-0 bg-[#1A1A1A]/40 backdrop-blur-sm" />
                    <div
                        className="relative w-full max-w-lg bg-[#FAF9F6] h-full overflow-y-auto p-10"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setSelected(null)}
                            data-testid="admin-detail-close"
                            className="text-[11px] uppercase tracking-[0.28em] text-[#1A1A1A]/55 hover:text-[#1B4332]"
                        >
                            ← Close
                        </button>
                        <h3 className="font-display text-3xl mt-4">{selected.name}</h3>
                        <div className="mt-2 flex items-center gap-3">
                            <StatusPill status={selected.status} />
                            <span className="text-[12px] text-[#1A1A1A]/55">
                                {new Date(selected.created_at).toLocaleString()}
                            </span>
                        </div>
                        <dl className="mt-8 space-y-5 text-[14px]">
                            <Row label="Service">{selected.service_type}</Row>
                            <Row label="Company">{selected.company || "—"}</Row>
                            <Row label="Email">
                                <a href={`mailto:${selected.email}`} className="link-underline">{selected.email}</a>
                            </Row>
                            <Row label="Phone">
                                <a href={`tel:${selected.phone}`} className="link-underline">{selected.phone}</a>
                            </Row>
                            <Row label="Guests">{selected.guest_count ?? "—"}</Row>
                            <Row label="Event Date">{selected.event_date || "—"}</Row>
                            <Row label="Message">
                                <p className="whitespace-pre-wrap">{selected.message}</p>
                            </Row>
                        </dl>

                        <div className="mt-10 border-t border-black/10 pt-6 flex flex-wrap gap-2">
                            {selected.status !== "reviewed" && (
                                <button
                                    onClick={async () => {
                                        await onStatus(selected.id, "reviewed");
                                        setSelected((s) => (s ? { ...s, status: "reviewed" } : s));
                                    }}
                                    data-testid="drawer-mark-reviewed"
                                    className="text-[11px] uppercase tracking-[0.26em] bg-[#1B4332] text-[#FAF9F6] px-4 py-2 hover:bg-[#112d21]"
                                >
                                    Mark Reviewed
                                </button>
                            )}
                            {selected.status !== "archived" && (
                                <button
                                    onClick={async () => {
                                        await onStatus(selected.id, "archived");
                                        setSelected((s) => (s ? { ...s, status: "archived" } : s));
                                    }}
                                    data-testid="drawer-archive"
                                    className="text-[11px] uppercase tracking-[0.26em] border border-black/15 px-4 py-2 hover:bg-[#1A1A1A] hover:text-[#FAF9F6]"
                                >
                                    Archive
                                </button>
                            )}
                            {selected.status !== "new" && (
                                <button
                                    onClick={async () => {
                                        await onStatus(selected.id, "new");
                                        setSelected((s) => (s ? { ...s, status: "new" } : s));
                                    }}
                                    data-testid="drawer-mark-new"
                                    className="text-[11px] uppercase tracking-[0.26em] border border-[#1B4332]/30 px-4 py-2 text-[#1B4332] hover:bg-[#1B4332]/10"
                                >
                                    Mark New
                                </button>
                            )}
                            <button
                                onClick={() => onDelete(selected.id)}
                                data-testid="drawer-delete"
                                className="ml-auto text-[11px] uppercase tracking-[0.26em] text-[#C84B31] border border-[#C84B31]/40 px-4 py-2 hover:bg-[#C84B31] hover:text-[#FAF9F6]"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}

function StatusPill({ status }) {
    const map = {
        new: "bg-[#1B4332] text-[#FAF9F6]",
        reviewed: "bg-[#E8EFEA] text-[#1B4332]",
        archived: "bg-[#1A1A1A]/15 text-[#1A1A1A]/70",
    };
    return (
        <span className={`text-[10px] uppercase tracking-[0.26em] px-2.5 py-1 ${map[status] || ""}`}>
            {status}
        </span>
    );
}

function Row({ label, children }) {
    return (
        <div className="grid grid-cols-3 gap-4">
            <dt className="text-[10px] uppercase tracking-[0.3em] text-[#1A1A1A]/55 col-span-1 pt-1">{label}</dt>
            <dd className="col-span-2">{children}</dd>
        </div>
    );
}
